import { useState } from 'react';
import { DateRange } from 'react-day-picker';
import { TDay, TDaysRange } from 'shared/types';
import { formatWithLocale } from '../../helpers/dateHelper';
import { useOutsideClickClose } from '../outsideClick/useOutsideClickClose';
import { setTimeToDateObject } from '../../../time';
import { TimeRange, useDatePickerParams } from './types';

export const useDatePicker = (params: useDatePickerParams) => {
	const { selectedDate, setSelectedDate } = params;

	const [daysRange, setDaysRange] = useState<DateRange>({
		from: undefined,
		to: undefined,
	});

	const [daysRangeInput, setDaysRangeInput] = useState<DateRange>({
		from: undefined,
		to: undefined,
	});

	const [isOpenCalendar, setOpenCalendar] = useState<boolean>(false);
	const [time, setTime] = useState('');
	const [timeRange, setTimeRange] = useState<TimeRange>({
		from: '00:00',
		to: '23:59',
	});

	const closeCalendar = () => {
		if (isOpenCalendar) {
			setOpenCalendar(false);
		}
	};

	useOutsideClickClose(closeCalendar, 'date-picker');

	const openCalendar = () => {
		if (isOpenCalendar) {
			setOpenCalendar(false);
		} else {
			setOpenCalendar(true);
		}
	};

	const selectDaysRangeAndCloseCalendar = (
		daysRange: TDaysRange | undefined,
	) => {
		if (daysRange?.to) {
			setOpenCalendar(false);
		}

		selectDaysRange(daysRange);
	};

	const selectDay = (day: TDay): Promise<any> | void => {
		if (day) {
			day = new Date(day);
			setSelectedDate(day);
			params?.select?.(day);
		}
	};

	const selectDaysRange = (daysRange: TDaysRange) => {
		if (daysRange?.from && timeRange?.from) {
			daysRange.from = setTimeToDateObject(
				timeRange.from,
				daysRange.from,
			);
		}

		if (daysRange?.to && timeRange?.to) {
			daysRange.to = setTimeToDateObject(timeRange.to, daysRange.to);
		}

		if (daysRange) {
			if (daysRange.from && daysRange.to) {
				setDaysRange({ from: undefined, to: undefined });
			} else {
				setDaysRange(daysRange);
			}
			setDaysRangeInput(daysRange);
			params?.selectRange?.(daysRange);
		}
	};

	const modifyTimeInDaysRange = (time: string, isStart: boolean = true) => {
		const prepareDaysRange: TDaysRange = {
			from: daysRange?.from,
			to: daysRange?.to,
		};

		const prepareTimeRange: TimeRange = {
			from: timeRange.from,
			to: timeRange.to,
		};

		if (isStart && daysRange?.from) {
			prepareTimeRange.from = time;
			prepareDaysRange.from = setTimeToDateObject(time, daysRange.from);
		} else if (daysRange?.to) {
			prepareTimeRange.to = time;
			prepareDaysRange.to = setTimeToDateObject(time, daysRange.to);
		}

		setDaysRange(prepareDaysRange);
		params?.selectRange?.(prepareDaysRange);
		setTimeRange(prepareTimeRange);
	};

	const modifyTimeInDate = (time: string) => {
		if (selectedDate) {
			const date = setTimeToDateObject(time, selectedDate);
			setSelectedDate(date);
			params?.select?.(date);
			setTime(time);
		}
	};


	return {
		formatWithLocale,
		openCalendar,
		selectDay,
		setSelectedDate,
		setDaysRange,
		selectDaysRangeAndCloseCalendar,
		modifyTimeInDaysRange,
		modifyTimeInDate,
		setDaysRangeInput,
		daysRange,
		daysRangeInput,
		isOpenCalendar,
		selectedDate,
		timeRange,
		time,
	};
};
